import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { DashboardSectionHeader } from './DashboardSectionHeader'
import { formatDashboardDateFr } from '../utils/semanticBadges'
import type { ProspectRecord } from '@/types/prospects'

// ---------------------------------------------------------------------------
// Badge styling helpers
// ---------------------------------------------------------------------------

function getPipelineStageLabel(stage: string, t: (key: string) => string) {
  const map: Record<string, string> = {
    suspect: t('dashboard.agentRecentProspects.stages.suspect'),
    prospect_froid: t('dashboard.agentRecentProspects.stages.cold'),
    prospect_tiede: t('dashboard.agentRecentProspects.stages.warm'),
    prospect_chaud: t('dashboard.agentRecentProspects.stages.hot'),
  }
  return map[stage] ?? stage
}

function pipelineStageBadgeClass(stage: string): string {
  switch (stage) {
    case 'prospect_chaud':
      return 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
    case 'prospect_tiede':
      return 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
    case 'prospect_froid':
      return 'border-transparent bg-sky-500/15 text-sky-900 dark:bg-sky-500/20 dark:text-sky-300'
    default:
      return 'border-transparent bg-muted text-muted-foreground'
  }
}

function getConversionStatusLabel(status: string, t: (key: string) => string) {
  const map: Record<string, string> = {
    open: t('dashboard.agentRecentProspects.conversionStatuses.open'),
    converted: t('dashboard.agentRecentProspects.conversionStatuses.converted'),
    lost: t('dashboard.agentRecentProspects.conversionStatuses.lost'),
    locked: t('dashboard.agentRecentProspects.conversionStatuses.locked'),
  }
  return map[status] ?? status
}

function conversionStatusBadgeClass(status: string): string {
  switch (status) {
    case 'converted':
      return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
    case 'lost':
      return 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
    case 'locked':
      return 'border-transparent bg-violet-500/15 text-violet-900 dark:bg-violet-500/20 dark:text-violet-300'
    default:
      return 'border-transparent bg-sky-500/15 text-sky-900 dark:bg-sky-500/20 dark:text-sky-300'
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AgentRecentProspectsTableProps {
  prospects: ProspectRecord[]
}

export function AgentRecentProspectsTable({ prospects }: AgentRecentProspectsTableProps) {
  const { t } = useTranslation()

  const headerActions = (
    <Button asChild variant="ghost" size="sm" className="gap-1.5">
      <Link to="/prospects">
        {t('dashboard.agentRecentProspects.viewAll')}
        <ArrowRight className="size-4" aria-hidden />
      </Link>
    </Button>
  )

  return (
    <>
      <DashboardSectionHeader title={t('dashboard.agentRecentProspects.title')} actions={headerActions} />

      {prospects.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border/80 bg-muted/10 py-8 text-center text-sm text-muted-foreground">
          {t('dashboard.agentRecentProspects.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('dashboard.agentRecentProspects.contact')}</TableHead>
                <TableHead className="hidden sm:table-cell">{t('dashboard.agentRecentProspects.company')}</TableHead>
                <TableHead>{t('dashboard.agentRecentProspects.pipeline')}</TableHead>
                <TableHead>{t('dashboard.agentRecentProspects.conversion')}</TableHead>
                <TableHead className="hidden md:table-cell">{t('dashboard.agentRecentProspects.submittedAt')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prospects.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">{p.contact_name}</p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {p.prospect_code ?? t('dashboard.codeUnavailable')}
                      </p>
                      <p className="truncate text-xs text-muted-foreground sm:hidden">
                        {p.company_name ?? '—'}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="hidden max-w-[10rem] truncate text-muted-foreground sm:table-cell">
                    {p.company_name ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={pipelineStageBadgeClass(p.pipeline_stage)}>
                      {getPipelineStageLabel(p.pipeline_stage, t)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={conversionStatusBadgeClass(p.conversion_status)}>
                      {getConversionStatusLabel(p.conversion_status, t)}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">
                    {formatDashboardDateFr(p.submitted_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

export function AgentRecentProspectsTableSkeleton() {
  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-40" />
      </div>
      <div className="overflow-x-auto rounded-lg bg-muted/10">
        <div className="space-y-3 p-3">
          <div className="grid grid-cols-[1.3fr_1.1fr_110px_110px_100px] gap-3">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
          </div>
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="grid grid-cols-[1.3fr_1.1fr_110px_110px_100px] gap-3">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-20 rounded-full" />
              <Skeleton className="h-9 w-20 rounded-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
